/**
 * MOSA-364, extended by MOSA-408. One parser for a pasted Google Sheets link.
 *
 * The wizard and the API must agree on what a link means, so this file is a
 * plain module with no Nest or React imports. The app carries the same rules in
 * `onlydash-api/src/database/connectors/sheets-link.ts`; the two test files
 * assert the same table of cases. Change them together.
 *
 * Accepted:
 *   https://docs.google.com/spreadsheets/d/<id>/edit#gid=0
 *   https://docs.google.com/spreadsheets/d/<id>/edit?gid=0#gid=0
 *   https://docs.google.com/spreadsheets/u/1/d/<id>/edit   (account hint)
 *   https://docs.google.com/u/1/spreadsheets/d/<id>/edit   (account hint)
 *   https://docs.google.com/spreadsheets/d/<id>
 *   bare <id> (legacy datasource rows store this)
 *   https://drive.google.com/file/d/<id>/view              (kind drive_file)
 *   https://drive.google.com/open?id=<id>                  (kind drive_file)
 *
 * A `drive_file` link is accepted with a reason: a Drive file can be an .xlsx
 * the user wants converted, so the probe reads `files.get` and decides. The
 * parser cannot know the type from the URL alone.
 *
 * Refused with a reason the wizard can act on:
 *   google_doc_link       /document/d/
 *   google_form_link      /forms/d/
 *   google_slides_link    /presentation/d/
 *   drive_folder_link     /drive/folders/<id>   (the wizard offers the Picker)
 *   published_copy_link   /spreadsheets/d/e/2PACX...
 *   excel_link            an explicit .xlsx/.xls/.csv or ?format=xlsx
 *   not_a_sheets_link     any other host or path
 *   missing_id            a Sheets path with no usable id
 */

export type SheetsLinkFailure =
  | "not_a_sheets_link"
  | "excel_link"
  | "missing_id"
  | "google_doc_link"
  | "google_form_link"
  | "google_slides_link"
  | "drive_folder_link"
  | "published_copy_link";

/**
 * `spreadsheet` is a native Google Sheet. `drive_file` is a Drive file id with
 * an unknown type; only the probe can say what it is.
 */
export type SheetsLinkKind = "spreadsheet" | "drive_file";

export type SheetsLink =
  | {
      ok: true;
      kind: SheetsLinkKind;
      /** Spreadsheet id, or the Drive file id when `kind` is `drive_file`. */
      spreadsheetId: string;
      /** Tab gid from ?gid= or #gid=, when the link carries one. */
      gid: number | null;
      /** The link as the user pasted it, trimmed. */
      raw: string;
      /** N from a `/u/N/` segment: which signed-in Google account opened it. */
      accountIndex: number | null;
    }
  | {
      ok: false;
      reason: SheetsLinkFailure;
      /** Set on `drive_folder_link`, so the wizard can open the Picker in it. */
      folderId?: string;
    };

/** Tolerates the `/u/N/` account segment Google puts in a copied URL. */
const SHEET_ID = /\/spreadsheets\/(?:u\/\d+\/)?d\/([^/?#]+)/;
/** A "publish to the web" copy. Read-only HTML, not an API-readable file. */
const PUBLISHED_COPY = /\/spreadsheets\/(?:u\/\d+\/)?d\/e\//;
const DOC_PATH = /\/document\/(?:u\/\d+\/)?d\//;
const FORM_PATH = /\/forms\/(?:u\/\d+\/)?d\//;
const SLIDES_PATH = /\/presentation\/(?:u\/\d+\/)?d\//;
const FOLDER_PATH = /\/(?:drive\/)?(?:u\/\d+\/)?folders\/([^/?#]+)/;
const DRIVE_FILE_PATH = /\/file\/(?:u\/\d+\/)?d\/([^/?#]+)/;
const ACCOUNT_HINT = /\/u\/(\d+)(?:\/|$)/;
/**
 * An .xlsx/.xls opened in Drive shows up as a file link, not a sheet link.
 * A Sheets export URL (`?format=xlsx`) counts too: the user is trying to bring
 * an Excel file, which this flow does not read.
 */
const EXCEL_HINT =
  /(\.(?:xlsx|xls|xlsm|csv)(?:[?#]|$)|[?&]format=(?:xlsx|xls|xlsm|csv)\b)/i;
const BARE_ID = /^[a-zA-Z0-9_-]{20,}$/;
/** Real ids are ~44 characters. Shorter means a truncated paste. */
const MIN_ID_LENGTH = 20;

/** 0 by default: Google's first tab is gid 0. */
export function parseSheetsLink(raw: string | null | undefined): SheetsLink {
  const text = (raw ?? "").trim();
  if (!text) {
    return { ok: false, reason: "missing_id" };
  }

  // Before SHEET_ID: /d/e/ would otherwise capture the literal "e".
  if (PUBLISHED_COPY.test(text)) {
    return { ok: false, reason: "published_copy_link" };
  }

  if (EXCEL_HINT.test(text)) {
    return { ok: false, reason: "excel_link" };
  }

  // A bare id (old datasource rows store the id, not a URL).
  if (BARE_ID.test(text) && !text.includes("/") && !text.includes(".")) {
    return {
      ok: true,
      kind: "spreadsheet",
      spreadsheetId: text,
      gid: null,
      raw: text,
      accountIndex: null,
    };
  }

  let url: URL | null = null;
  try {
    url = new URL(text);
  } catch {
    url = null;
  }

  if (url) {
    const host = url.hostname.toLowerCase();
    const isGoogle =
      host === "docs.google.com" ||
      host === "drive.google.com" ||
      host.endsWith(".googleusercontent.com");
    if (!isGoogle) {
      return { ok: false, reason: "not_a_sheets_link" };
    }
  }

  const accountIndex = accountIndexFrom(text);
  const spreadsheetId = text.match(SHEET_ID)?.[1];

  if (spreadsheetId) {
    if (spreadsheetId.length < MIN_ID_LENGTH) {
      return { ok: false, reason: "missing_id" };
    }
    return {
      ok: true,
      kind: "spreadsheet",
      spreadsheetId,
      gid: gidFrom(text, url),
      raw: text,
      accountIndex,
    };
  }

  // Another Google editor. Each one gets its own message, because the fix
  // differs: a Form has a responses sheet, a Doc has nothing to read.
  if (DOC_PATH.test(text)) {
    return { ok: false, reason: "google_doc_link" };
  }
  if (FORM_PATH.test(text)) {
    return { ok: false, reason: "google_form_link" };
  }
  if (SLIDES_PATH.test(text)) {
    return { ok: false, reason: "google_slides_link" };
  }

  const folderId = text.match(FOLDER_PATH)?.[1];
  if (folderId) {
    return { ok: false, reason: "drive_folder_link", folderId };
  }

  // A Drive file: the type is unknown here, so the probe reads files.get.
  const driveFileId = text.match(DRIVE_FILE_PATH)?.[1] ?? openIdFrom(url);
  if (driveFileId) {
    if (driveFileId.length < MIN_ID_LENGTH) {
      return { ok: false, reason: "missing_id" };
    }
    return {
      ok: true,
      kind: "drive_file",
      spreadsheetId: driveFileId,
      gid: gidFrom(text, url),
      raw: text,
      accountIndex,
    };
  }

  return { ok: false, reason: "not_a_sheets_link" };
}

/** `https://drive.google.com/open?id=<id>` is still a common share shape. */
function openIdFrom(url: URL | null): string | null {
  if (!url || !/\/open$/.test(url.pathname)) {
    return null;
  }
  const id = url.searchParams.get("id");
  return id && BARE_ID.test(id) ? id : null;
}

function accountIndexFrom(text: string): number | null {
  const index = Number(text.match(ACCOUNT_HINT)?.[1]);
  return Number.isInteger(index) ? index : null;
}

function gidFrom(text: string, url: URL | null): number | null {
  const query = url?.searchParams.get("gid");
  const hash = text.match(/[#&]gid=(\d+)/);
  const value = hash?.[1] ?? (query && /^\d+$/.test(query) ? query : null);
  if (value == null) {
    return null;
  }
  const gid = Number(value);
  return Number.isInteger(gid) ? gid : null;
}

/**
 * The message the wizard shows for a refusal. One table, so the API error body
 * and the app's instant feedback cannot drift apart.
 */
const LINK_FAILURE_MESSAGE: Record<SheetsLinkFailure, string> = {
  not_a_sheets_link:
    "That is not a Google Sheets link. Open the sheet, then copy the link from the address bar.",
  excel_link: "That is an Excel file. Paste a Google Sheets link.",
  missing_id:
    "That link has no spreadsheet id. Copy the whole link from your browser.",
  google_doc_link:
    "That is a Google Doc, not a Google Sheet. OnlyDash reads a sheet with rows and columns.",
  google_form_link:
    "That is a Google Form, not a Google Sheet. Open the form responses, choose the spreadsheet, then paste its link.",
  google_slides_link:
    "That is a Google Slides file, not a Google Sheet. OnlyDash reads a sheet with rows and columns.",
  drive_folder_link:
    "That is a Drive folder. Choose one sheet in the folder, then paste its link.",
  published_copy_link:
    "That is a published copy of a sheet. Open the sheet itself, then copy the link from the address bar.",
};

export function sheetsLinkMessage(reason: SheetsLinkFailure): string {
  return LINK_FAILURE_MESSAGE[reason] ?? LINK_FAILURE_MESSAGE.not_a_sheets_link;
}

/** The canonical link we echo back, so the user sees what we understood. */
export function canonicalSheetsUrl(
  spreadsheetId: string,
  gid?: number | null,
): string {
  const base = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
  return gid == null ? base : `${base}#gid=${gid}`;
}

/** MOSA-408. The same echo for a Drive file whose type the probe decides. */
export function canonicalDriveFileUrl(fileId: string): string {
  return `https://drive.google.com/file/d/${fileId}/view`;
}

/** The link to echo back for either kind. */
export function canonicalLinkUrl(link: SheetsLink): string | undefined {
  if (!link.ok) {
    return undefined;
  }
  return link.kind === "drive_file"
    ? canonicalDriveFileUrl(link.spreadsheetId)
    : canonicalSheetsUrl(link.spreadsheetId, link.gid);
}

/** How many characters of a sheet name the step rail shows. */
const RAIL_LABEL_MAX = 34;

/**
 * MOSA-697 item 18. The short label for the step rail. A full sheet URL wraps
 * over three lines there and moves every step under it, so the rail shows the
 * sheet name when the link check reported one, and a shortened link when it
 * did not.
 */
export function shortSheetLabel(args: {
  title?: string | null;
  spreadsheetId: string;
}): string {
  const title = (args.title ?? "").trim();
  if (title) {
    return title.length > RAIL_LABEL_MAX
      ? `${title.slice(0, RAIL_LABEL_MAX - 1).trimEnd()}…`
      : title;
  }
  const id = (args.spreadsheetId ?? "").trim();
  if (!id) {
    return "";
  }
  return id.length > 8
    ? `docs.google.com/…/${id.slice(0, 8)}…`
    : `docs.google.com/…/${id}`;
}

/** What a valid link looks like, for the "not a Sheets link" message. */
export const SHEETS_LINK_EXAMPLE =
  "https://docs.google.com/spreadsheets/d/1AbCdEfGhIjKlMnOpQrStUvWxYz0123456789/edit";
